import {createStyles} from '@mantine/core';
import React from 'react';

import {QueryingChartContent} from 'charts/chart-content/ChartContent';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Pane from 'ui-library/pane/Pane';

import {getOutputNameFromOutput, isSendToDocumentEnabled} from './OutputsUtil';
import ScratchPlateOutputPreview from './ScratchPlateOutputPreview';
import {
  AnalysisOutput,
  AnalysisOutputConfig,
  OutputDocumentLayoutConfiguration,
  OutputDocumentLayoutItemType,
  AnalysisOutputType,
} from './Types';

export const PREVIEW_ROW_LIMIT = 5;

export interface OutputPreviewProps {
  outputs: Array<AnalysisOutput>;
  pipeline: PipelineGraphQL;
  clientStateId: string;
  documentLayoutConfiguration: OutputDocumentLayoutConfiguration;
  useAnalysisNamePlaceholder?: boolean;
}

function SingleOutputPreview({
  index,
  outputConfig,
  pipeline,
  clientStateId,
  useAnalysisNamePlaceholder,
}: {
  index: number;
  outputConfig: AnalysisOutputConfig;
  pipeline: PipelineGraphQL;
  clientStateId: string;
  useAnalysisNamePlaceholder: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const analysisName = useAnalysisNamePlaceholder ? 'New Analysis' : pipeline.name;
  if (outputConfig.type === AnalysisOutputType.CHART) {
    return (
      <div key={`preview-${index}`} className={classes.chartPreview}>
        <Pane title={getOutputNameFromOutput(analysisName, outputConfig)}>
          <QueryingChartContent chartId={outputConfig.chart.id} />
        </Pane>
      </div>
    );
  }
  if (outputConfig.type === AnalysisOutputType.PLATE) {
    return (
      <ScratchPlateOutputPreview
        key={`preview-${index}`}
        scratchPlates={outputConfig.scratchPlates}
        title={outputConfig.edgeLabel}
      />
    );
  }
  return (
    <div className={classes.previewTable} key={`preview-${index}`}>
      <PersistedDataFrameTableWrapper
        actionOrigin={ActionOrigin.ANALYSIS_TOOL}
        clientStateId={clientStateId}
        dataFrameId={outputConfig.dataFrame.id}
        dataFrameNameOverride={getOutputNameFromOutput(analysisName, outputConfig)}
        rowLimit={PREVIEW_ROW_LIMIT}
        key={`preview-table-${index}`}
      />
    </div>
  );
}

export default function OutputPreview({
  outputs,
  pipeline,
  clientStateId,
  documentLayoutConfiguration,
  useAnalysisNamePlaceholder = false,
}: OutputPreviewProps): JSX.Element {
  const {classes} = useStyles();
  if (!isSendToDocumentEnabled()) {
    return (
      <div className={classes.previewWrapper}>
        {outputs.map((output, index) => (
          <SingleOutputPreview
            key={`single-output-preview-${index}`}
            index={index}
            outputConfig={output.outputConfig}
            pipeline={pipeline}
            clientStateId={clientStateId}
            useAnalysisNamePlaceholder={useAnalysisNamePlaceholder}
          />
        ))}
      </div>
    );
  }
  return (
    <div className={classes.previewWrapper}>
      {documentLayoutConfiguration.map((layoutItem, originalIndex) => {
        if (layoutItem.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE) {
          return (
            <div key={`preview-wrapper-${originalIndex}`} className={classes.sectionHeader}>
              <span className={classes.sectionName}>{layoutItem.title}</span>
              <div className={classes.line} />
            </div>
          );
        }
        return (
          <SingleOutputPreview
            key={`single-output-preview-${originalIndex}`}
            index={originalIndex}
            outputConfig={layoutItem.outputConfig}
            pipeline={pipeline}
            clientStateId={clientStateId}
            useAnalysisNamePlaceholder={useAnalysisNamePlaceholder}
          />
        );
      })}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  chartPreview: {
    height: '400px',
    minHeight: '400px',
  },
  previewWrapper: {
    display: 'grid',
    gridAutoRows: 'max-content',
    height: '100%',
    overflowY: 'auto',
    rowGap: theme.spacing.lg,
    padding: theme.spacing.md,
  },
  previewTable: {
    height: 'fit-content',
  },
  sectionName: {
    ...FONT_PROXIMA_13,
    fontWeight: theme.other.fontWeights.semibold,
    marginRight: 6,
  },
  sectionHeader: {
    alignItems: 'center',
    display: 'flex',
  },
  line: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    flexGrow: 1,
  },
}));
